import React, { useCallback, FC } from "react";
import cn from "clsx";
import { css } from "linaria";
import Carousel from "react-bootstrap/Carousel";
import { useDrag } from "@use-gesture/react";

import AuthenticatedAppLink from "../AuthenticatedAppLink";
import Composition from "../Composition";

import useMobile from "../../hooks/useMobile";

import { useAuthState } from "../../contexts/Auth/context";

import colors from "../../utils/colorConstants";
import fastpanel from "../../utils/fastpanel";
import { buildFlyerCustomizeURL } from "../../utils/urls";
import { isCompositionPremium } from "../../utils/compositions";

interface Props {
  className: any;
  comps: any[];
  toggleValue: number;
  toggleCallback: any;
  customizeURL: string;
}

const CarouselStyles = css`
  position: relative;
  margin: 0.5rem 20% 3rem;
  @screen lg {
    margin: 0 10%;
    overflow: visible;
  }
  & .carousel-inner {
    position: relative;
    overflow: visible;
    display: flex;
    transition: right 0.25s ease-out;
    flex-direction: row;
  }
  &.first-selected .carousel-inner {
    right: 0;
  }
  &.second-selected .carousel-inner {
    right: 100%;
  }
  & .carousel-indicators {
    position: absolute;
    display: flex;
    padding: 0;
    justify-content: center;
    align-items: center;
    z-index: 2;
    margin: 0 auto;
    bottom: -2.25rem;
    width: 100%;
    @screen lg {
      top: 0;
      bottom: auto;
      margin-top: 57.5%;
    }
    button {
      border-radius: 50%;
      width: 1.25rem;
      height: 1.25rem;
      list-style: none;
      background-color: ${colors.alto};
      margin: 0 0.5rem;
      @screen lg {
        width: 1rem;
        height: 1rem;
        margin: 0 0.25rem;
      }
      &.active {
        background-color: ${colors.gatsby};
      }
      &:last-child {
        display: none;
      }
    }
  }
  @screen lg {
    & .carousel-inner {
      width: 100%;
    }
  }
`;

const FlyerCarousel: FC<Props> = (props) => {
  const {
    className,
    comps,
    toggleValue,
    toggleCallback,
  } = props;
  const { isLoggedIn } = useAuthState();
  const isMobile = useMobile();
  // NOTE: This doesn't fire for the out of focus comp because it has `pointer-events: none;`
  const compClick = (index: number) => {
    if (isLoggedIn) {
      const comp = comps[index];
      fastpanel(
        {
          name: "pbr_link_clicked",
          data: {
            "id": "Flyer comp",
            "href": buildFlyerCustomizeURL(comp.id),
            "ispremium": isCompositionPremium(comp),
            "variant_type": comp.is_variant ? 'variant' : comps?.length > 1 ? 'primary' : 'none'
          }
        }
      );
    }
  };
  let indicatorSelectCallback = useCallback(
    (index: number) => {
      toggleCallback(index, "carousel_indicator");
    },
    [toggleCallback]
  );
  const bindDragGesture = useDrag(
    (gesture) => {
      if (!isMobile) return;
      const { swipe: [swipeX] } = gesture;
      if (swipeX !== 0) {
        toggleCallback(toggleValue + -1 * swipeX, "swipe");
      }
    },
    { swipe: { distance: [10, 10] } }
  );

  return (
    <Carousel
      className={cn(
        className,
        CarouselStyles,
        toggleValue === 0 ? "first-selected" : "second-selected"
      )}
      // @ts-expect-error
      onSelect={indicatorSelectCallback}
      activeIndex={toggleValue}
      controls={false}
      indicators={comps.length > 1}
      interval={null}
      pause={false}
      slide={false}
      wrap={false}
      touch={false}
      {...bindDragGesture()}
    >
      {comps.map((composition, index) => {
        return (
          <div
            key={composition.id}
            className="flex relative cursor-pointer flex-grow flex-shrink-0 w-full"
            onClick={() => toggleCallback(index, "comp_image")}
          >
            <AuthenticatedAppLink
              className={
                cn(
                  "block m-auto w-[85%] transition-[width] duration-500 ease-out",
                  {
                    "!w-full pointer-events-auto": toggleValue === index,
                    "pointer-events-none": toggleValue !== index,
                  }
                )
              }
              href={buildFlyerCustomizeURL(composition.id)}
              onClick={() => compClick(index)}
              tracking={
                {
                  "id": "Flyer comp",
                  "ispremium": isCompositionPremium(composition),
                  "variant_type": composition.is_variant
                    ? 'variant'
                    : comps?.length > 1 ? 'primary' : 'none',
                }
              }
              rel="nofollow,noindex"
            >
              <Composition composition={composition} priority={true} />
            </AuthenticatedAppLink>
          </div>
        );
      })}
      <div className="flex relative flex-grow flex-shrink-0 cursor-pointer w-full pointer-events-none">
        <div className="w-full mb-[216.53%] lg:mb-[53.88%]" />
      </div>
    </Carousel>
  );
};

export default FlyerCarousel;